import { useState } from 'react';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaUser, FaCommentDots } from 'react-icons/fa';
import { submitContactForm } from '../../services/contactService';
import './contact-form.css';
import PhoneIcon from '@mui/icons-material/Phone';
import IndiaFlag from '../../assets/img/Flags/indian.svg';
import UKFlag from '../../assets/img/Flags/Uk.svg';

const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phone') {
            const onlyNums = value.replace(/[^0-9]/g, '');
            if (onlyNums.length > 10) return;
            if (onlyNums.length > 0 && !/^[6-9]/.test(onlyNums)) return;

            setFormData(prev => ({
                ...prev,
                [name]: onlyNums
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const validatePhone = (phone) => {
        const cleaned = phone.replace(/\D/g, '');
        return cleaned.length >= 10;
    };

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!validateEmail(formData.email)) {
            setStatus({ type: 'error', message: 'Please enter a valid email address.' });
            return;
        }
        if (!validatePhone(formData.phone)) {
            setStatus({ type: 'error', message: 'Please enter a valid phone number (at least 10 digits).' });
            return;
        }

        setLoading(true);
        setStatus({ type: '', message: '' });

        try {
            const response = await submitContactForm(formData);
            if (response.success) {
                setStatus({ type: 'success', message: 'Message sent successfully! We will get back to you soon.' });
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                });

                // Clear message after 5 seconds
                setTimeout(() => {
                    setStatus({ type: '', message: '' });
                }, 5000);
            } else {
                setStatus({ type: 'error', message: response.message || 'Something went wrong. Please try again.' });
                setTimeout(() => setStatus({ type: '', message: '' }), 5000);
            }
        } catch (error) {
            console.error('Contact form submission error:', error);
            setStatus({ type: 'error', message: 'Failed to send message. Please check your internet connection and try again.' });
            setTimeout(() => setStatus({ type: '', message: '' }), 5000);
        } finally {
            setLoading(false);
        }
    };

    const contactInfo = [
        {
            icon: <FaPhone />,
            title: 'Phone Number',
            details: ['+91 78930 92211'],
            color: '#3b82f6'
        },
        {
            icon: <FaEnvelope />,
            title: 'Email Address',
            details: ['info@royalespaces.com'],
            color: '#10b981'
        },
        {
            icon: <FaMapMarkerAlt />,
            title: 'Office Location',
            addresses: [
                {
                    flag: IndiaFlag,
                    flagAlt: 'India',
                    lines: ['Amaravati, Andhra Pradesh, India']
                },
                {
                    flag: UKFlag,
                    flagAlt: 'United Kingdom',
                    lines: ['18 Lincoln Drive, Houlton, Rugby', 'Warwickshire, CV23 1BS, UK']
                }
            ],
            color: '#f97316'
        }
    ];

    return (
        <section className="contact-section">
            <div className="container">
                <div className="contact-header">
                    <h2>Get In Touch</h2>
                    <p>Have questions? We're here to help you find your perfect property</p>
                </div>

                <div className="contact-content">
                    {/* Contact Info Cards */}
                    <div className="contact-info-grid">
                        {contactInfo.map((info, index) => (
                            <div key={index} className="contact-info-card">
                                <div className="contact-info-icon" style={{ background: info.color }}>
                                    {info.icon}
                                </div>
                                <h3>{info.title}</h3>
                                {info.details && info.details.map((detail, idx) => (
                                    <p key={idx}>{detail}</p>
                                ))}
                                {info.addresses && info.addresses.map((addr, addrIdx) => (
                                    <div key={addrIdx} className="contact-address-block">
                                        <img src={addr.flag} alt={addr.flagAlt} className="contact-address-flag" />
                                        <div className="contact-address-lines">
                                            {addr.lines.map((line, lineIdx) => (
                                                <p key={lineIdx}>{line}</p>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* Contact Form */}
                    <div className="contact-form-wrapper">
                        <div className="form-container">
                            <h3>Send Us A Message</h3>
                            <form onSubmit={handleSubmit}>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="name">
                                            <FaUser /> Full Name
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Enter your name"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="email">
                                            <FaEnvelope /> Email Address
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Enter your email"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="phone">
                                            <PhoneIcon sx={{ fontSize: { xs: 14, sm: 18 }, color: '#d32f2f' }} />
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="Enter your phone"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="subject">
                                            <FaCommentDots /> Subject
                                        </label>
                                        <input
                                            type="text"
                                            id="subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            placeholder="Enter subject"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="message">
                                        <FaCommentDots /> Your Message
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Tell us about your requirements..."
                                        rows="6"
                                        required
                                    ></textarea>
                                </div>

                                {status.message && (
                                    <div className={`form-status ${status.type}`}>
                                        {status.message}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="submit-btn"
                                    disabled={loading}
                                >
                                    {loading ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactForm;
